import React from "react";
import { Link } from "react-router-dom";
import { Badge, Button } from "reactstrap";
import { compose } from "recompose";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";

import { AuthState } from "@auth/models";
import { Localisation } from "@localisation/index";
import { Locale, LocalisationState } from "@localisation/models";
import { getLocale } from "@localisation/selectors";
import { canUpdateAlbum as canUpdateAlbumSelector } from "@music/selectors";
import { DataRow, DataRowDate, DataRowTranslated } from "@ui/DataRow";
import { messages as uiMessages } from "@ui/messages";
import { AlbumPack } from "../AlbumPack";
import { ArtistsList } from "../ArtistsList";
import { Copyright } from "../Copyright";
import { Album as Model, Artist } from "../../models";
import { messages } from "../../messages";
import { WaIcon } from "@app/modules/ui/WaIcon";

export interface AlbumProps {
    album: Model;
    style?: "headline";
    small?: boolean;
    link?: boolean;
    children?: React.ReactNode;
    additionalDetails?: boolean;
}

interface AlbumStateProps {
    locale: Locale;
    canUpdateAlbum: boolean;
}

type Props = AlbumProps & AlbumStateProps;
type State = LocalisationState & AuthState;

const Album = ({ album, style, small, link, children, locale, additionalDetails, canUpdateAlbum }: Props) => {
    // Build the info.
    const info: JSX.Element[] = [];
    let className = "album";
    let TitleTag = "span";
    let TitleClass = "album__title";
    const ArtistsTag = "span";
    if (style === "headline") {
        className += " album--headline";
        TitleTag = "h1";
        TitleClass += " display-1";
    }
    else {
        className += " album--list-item";
    }
    if (album != null) {
        if (album.isDraft()) {
            info.push(
                <Badge key="badge" className="album__badge text-dark" color="warning">
                    <FormattedMessage {...messages.draftAlbum} />
                </Badge>,
            );
        }
        else if (album.isArchived()) {
            info.push(
                <Badge key="badge" className="album__badge text-dark" color="light">
                    <FormattedMessage {...messages.archivedAlbum} />
                </Badge>,
            );
        }
        if (album.format != null) {
            info.push(
                <h5 key="format" className="album__format">
                    <WaIcon size="sm" name="product"></WaIcon>
                    {" "}
                    {Localisation.translateMap(album.format.label, locale)}
                </h5>,
            );
        }
        const titleChildren: React.ReactNode[] = [album.getTitle()];
        if (album.titleVersion != null && album.titleVersion !== "") {
            titleChildren.push(
                <>&nbsp;</>,
                <small key="version" className="album__title__version">
                    ({album.titleVersion})
                </small>,
            );
        }
        let title = React.createElement(
            TitleTag,
            { key: "title", className: TitleClass },
            ...titleChildren,
        );
        const url = album.getViewUrl();
        if (url != null && (link == null || link)) {
            title = <Link key="title" to={url} className="album__link">{title}</Link>;
        }
        info.push(title);
        if (album.artists != null && album.artists.length > 0) {
            info.push(
                React.createElement(
                    ArtistsTag,
                    { key: "artists", className: "album__artists" },
                    <ArtistsList showImage artists={album.artists as Artist[]} />,
                ),
            );
        }
        if (style === "headline" && canUpdateAlbum) {
            info.push(
                <Link
                    key="edit-button"
                    to={album.getEditUrl()}
                    className="album__edit-btn"
                >
                    <WaIcon name="pencil" />
                    <FormattedMessage {...uiMessages.buttonEdit} />
                </Link>,
            );
        }
        if (additionalDetails) {
            const details: JSX.Element[] = [];
            if (album.upc) {
                details.push(
                    <DataRow
                        key="upc"
                        message={messages.albumColumnUpc}
                        value={album.upc}
                    />,
                );
            }
            if (album.customId) {
                details.push(
                    <DataRow
                        key="customId"
                        message={messages.albumColumnCustomId}
                        value={album.customId}
                    />,
                );
            }
            if (album.originalReleaseDate) {
                details.push(
                    <DataRowDate
                        key="originalReleaseDate"
                        message={messages.albumColumnOriginalReleaseDate}
                        value={album.originalReleaseDate}
                    />,
                );
            }
            if (album.preorderDate) {
                details.push(
                    <DataRowDate
                        key="preorderDate"
                        message={messages.albumColumnPreorderDate}
                        value={album.preorderDate}
                    />,
                );
            }
            if (album.releaseDate) {
                details.push(
                    <DataRowDate
                        key="releaseDate"
                        message={messages.albumColumnReleaseDate}
                        value={album.releaseDate}
                    />,
                );
            }
            if (album.label) {
                details.push(
                    <DataRow
                        key="label"
                        message={messages.albumColumnLabel}
                        value={album.label.name}
                    />,
                );
            }
            if (album.genre) {
                details.push(
                    <DataRowTranslated
                        key="genre"
                        message={messages.albumColumnGenre}
                        value={album.genre.label}
                        locale={locale}
                    />,
                );
            }
            if (album.additionalGenre) {
                details.push(
                    <DataRowTranslated
                        key="additionalGenre"
                        message={messages.albumColumnAdditionalGenre}
                        value={album.additionalGenre.label}
                        locale={locale}
                    />,
                );
            }
            info.push(
                <div key="details" className="album__details">
                    <h4 className="display-6">
                        <FormattedMessage {...messages.productInformation} />
                    </h4>
                    <div className="album__details__grid">
                        {details}
                    </div>
                    <Copyright key="copyright" entity={album} />
                </div>,
            );
        }
    }
    return (
        <div className={className}>
            <div className="album__artwork">
                <AlbumPack
                    album={album}
                    size={small ? "small" : undefined}
                    link={link}
                    />
            </div>
            <div className="album__info">{info}</div>
            <div className="album__children">{children}</div>
        </div>
    );
};

const mapStateToProps = (state: State) => {
    return {
        locale: getLocale(state),
        canUpdateAlbum: canUpdateAlbumSelector(state),
    };
};

const ConnectedAlbum = compose<Props, AlbumProps>(
    connect(mapStateToProps, null),
)(Album);

export {
    Album as AlbumPresenter,
    ConnectedAlbum as Album,
};
